/**
 * Input sanitization utilities to prevent XSS attacks
 * Now using DOMPurify for robust, production-grade HTML sanitization
 */

import DOMPurify from 'dompurify';

/**
 * Sanitize HTML to remove dangerous scripts and tags using DOMPurify
 * @param input HTML string to sanitize
 * @param allowedTags Optional array of allowed HTML tags (default: none, plain text only)
 */
export const sanitizeHtml = (input: string, allowedTags?: string[]): string => {
    if (!input) return '';

    // Configure DOMPurify options
    const config = {
        ALLOWED_TAGS: allowedTags || [], // If empty, strips all HTML
        ALLOWED_ATTR: ['href', 'title', 'target', 'rel'], // Safe attributes
        ALLOW_DATA_ATTR: false, // Block data-* attributes
        FORBID_TAGS: ['script', 'style', 'iframe', 'object', 'embed', 'form', 'input'], // Extra protection
        FORBID_ATTR: ['onerror', 'onload', 'onclick', 'onmouseover'], // Block event handlers
        KEEP_CONTENT: true, // Keep text content when removing tags
        RETURN_TRUSTED_TYPE: false,
    } as const;

    // If allowing links, ensure they're safe  
    const finalConfig: any = { ...config };
    if (allowedTags?.includes('a')) {
        finalConfig.ALLOWED_URI_REGEXP = /^(?:(?:(?:f|ht)tps?|mailto|tel):)/i; // Only http(s), mailto, tel
    }

    // Ensure we return a string, not TrustedHTML
    // Temporarily suppress specific DOMPurify warnings about intrinsics
    const originalWarn = console.warn;
    console.warn = (...args) => {
        if (typeof args[0] === 'string' && args[0].includes('Removing unpermitted intrinsics')) {
            return;
        }
        originalWarn(...args);
    };

    const result = String(DOMPurify.sanitize(input, finalConfig));
    console.warn = originalWarn;

    return result;
};

/**
 * Sanitize text input by removing all HTML (plain text only)
 */
export const sanitizeText = (input: string): string => {
    if (!input) return '';

    // DOMPurify with no allowed tags = plain text
    // Temporarily suppress specific DOMPurify warnings about intrinsics
    const originalWarn = console.warn;
    console.warn = (...args) => {
        if (typeof args[0] === 'string' && args[0].includes('Removing unpermitted intrinsics')) {
            return;
        }
        originalWarn(...args);
    };

    const sanitized = DOMPurify.sanitize(input, {
        ALLOWED_TAGS: [],
        KEEP_CONTENT: true
    });

    console.warn = originalWarn;

    return String(sanitized).trim();
};

/**
 * Sanitize price input - ensure valid number, no negatives
 */
export const sanitizePrice = (input: string | number | null | undefined): number => {
    if (input === null || input === undefined || input === '') return 0;

    const num = typeof input === 'string'
        ? parseFloat(input.replace(/[^\d.-]/g, ''))
        : Number(input);

    return isNaN(num) || num < 0 ? 0 : Math.min(num, 999999999);
};

/**
 * Sanitize and validate email
 */
export const sanitizeEmail = (email: string): string => {
    if (!email) return '';

    return email
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9@._-]/g, '');
};

/**
 * Sanitize phone number (keep only numbers, +, -, (, ))
 */
export const sanitizePhone = (phone: string): string => {
    if (!phone) return '';

    return phone.replace(/[^\d+\-()]/g, '');
};

/**
 * Sanitize URL to prevent javascript: and data: protocols
 */
export const sanitizeUrl = (url: string): string => {
    if (!url) return '';

    const trimmed = url.trim();

    // Block dangerous protocols
    if (trimmed.match(/^(javascript|data|vbscript):/i)) {
        return '';
    }

    return trimmed;
};

/**
 * Escape SQL-like characters (for search queries)
 */
export const escapeSqlLikeChars = (input: string): string => {
    if (!input) return '';

    return input
        .replace(/%/g, '\\%')
        .replace(/_/g, '\\_');
};

/**
 * Rate limiting helper for preventing spam
 */
class RateLimiter {
    private attempts: Map<string, number[]> = new Map();

    /**
     * Check if action is allowed based on rate limit
     * @param key Unique identifier (e.g., user email, IP)
     * @param maxAttempts Maximum attempts allowed
     * @param windowMs Time window in milliseconds
     * @returns true if allowed, false if rate limited
     */
    isAllowed(key: string, maxAttempts: number = 5, windowMs: number = 60000): boolean {
        const now = Date.now();
        const userAttempts = this.attempts.get(key) || [];

        // Filter out old attempts outside the time window
        const recentAttempts = userAttempts.filter(time => now - time < windowMs);

        if (recentAttempts.length >= maxAttempts) {
            return false;
        }

        // Add current attempt
        recentAttempts.push(now);
        this.attempts.set(key, recentAttempts);

        return true;
    }

    /**
     * Reset attempts for a key
     */
    reset(key: string): void {
        this.attempts.delete(key);
    }
}

export const loginRateLimiter = new RateLimiter();
export const registrationRateLimiter = new RateLimiter();
